import React, { useState, useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { sanitizeSearchQuery } from '../utils/textSanitize';
import { useAuth } from '../contexts/AuthContext';
import { 
  Search, 
  Filter, 
  Eye, 
  Plus, 
  Heart, 
  MessageCircle, 
  DollarSign, 
  Calendar, 
  Package, 
  Building, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Shield,
  X,
  Star
} from 'lucide-react';

const MarketplacePage = () => {
  const { user, hasPermission, getEmpresaId, isRole } = useAuth();
  const [propostas, setPropostas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // create, view, respond
  const [selectedProposta, setSelectedProposta] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    abertas: 0,
    em_andamento: 0,
    finalizadas: 0,
    valor_total: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0
  });

  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    loadPropostas();
  }, [pagination.page, statusFilter, categoryFilter]);

  useEffect(() => {
    // Debounce search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      if (searchTerm !== '') {
        loadPropostas();
      }
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Função auxiliar para extrair mensagem de erro específica
  const getErrorMessage = (error) => {
    console.log('🔍 Analisando erro:', error);
    
    // Verificar se é um erro do Firebase Functions
    if (error.code && error.code.startsWith('functions/')) {
      return error.message || 'Erro na validação dos dados';
    }
    
    // Verificar códigos específicos
    if (error.code === 'functions/invalid-argument') {
      return error.message || 'Dados inválidos fornecidos';
    }
    
    if (error.code === 'functions/permission-denied') {
      return 'Você não tem permissão para realizar esta operação';
    }
    
    if (error.code === 'functions/not-found') {
      return 'Proposta não encontrada';
    }
    
    if (error.code === 'functions/already-exists') {
      return 'Você já demonstrou interesse nesta proposta';
    }
    
    // Verificar se há uma mensagem específica no erro
    if (error.message && error.message !== 'INTERNAL') {
      return error.message;
    }
    
    // Fallback para erro genérico
    return 'Erro ao conectar com o servidor';
  };

  const loadPropostas = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 MarketplacePage: Carregando propostas...');

      const listPropostas = httpsCallable(functions, 'listPropostas');
      const result = await listPropostas({
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm?.trim() ? sanitizeSearchQuery(searchTerm) : undefined,
        status: statusFilter || undefined,
        categoria: categoryFilter || undefined,
        empresaId: isRole('superadmin') ? undefined : getEmpresaId()
      });

      console.log('🔍 MarketplacePage: Resultado da listPropostas:', result);

      if (result.data && result.data.success) {
        const data = result.data.data;
        
        setPropostas(data.items || []);
        
        // Calcular estatísticas
        const items = data.items || [];
        const statsData = {
          total: items.length,
          abertas: items.filter(p => p.status === 'aberta').length,
          em_andamento: items.filter(p => p.status === 'em_andamento').length,
          finalizadas: items.filter(p => p.status === 'finalizada').length,
          valor_total: items.reduce((sum, p) => sum + (p.valor_estimado || 0), 0)
        };
        setStats(statsData);
        
        setPagination(prev => ({
          ...prev,
          total: data.pagination?.total || 0,
          totalPages: data.pagination?.totalPages || 0
        }));
        
        console.log('✅ MarketplacePage: Propostas carregadas:', data.items?.length || 0);
      } else {
        throw new Error(result.data?.message || 'Erro ao carregar propostas');
      }

    } catch (error) {
      console.error('❌ MarketplacePage: Erro ao carregar propostas:', error);
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      setPropostas([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProposta = () => {
    setSelectedProposta(null);
    setModalMode('create');
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleViewProposta = (proposta) => {
    setSelectedProposta(proposta);
    setModalMode('view');
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleRespondProposta = (proposta) => {
    setSelectedProposta(proposta);
    setModalMode('respond');
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleSaveProposta = async (propostaData) => {
    try {
      setError('');
      setSuccess('');
      
      if (modalMode === 'create') {
        const createProposta = httpsCallable(functions, 'createProposta');
        const result = await createProposta({
          ...propostaData,
          empresaId: isRole('superadmin') ? undefined : getEmpresaId()
        });
        
        console.log('✅ Proposta criada:', result);
        setSuccess('Proposta criada com sucesso!');
      } else if (modalMode === 'respond') {
        const respondProposta = httpsCallable(functions, 'respondProposta');
        const result = await respondProposta({
          propostaId: selectedProposta.id,
          ...propostaData
        });
        
        console.log('✅ Resposta enviada:', result);
        setSuccess('Resposta enviada com sucesso!');
      }
      
      await loadPropostas();
      setShowModal(false);
      
    } catch (error) {
      console.error('❌ Erro ao salvar proposta:', error);
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
    }
  };

  const handleDemonstrarInteresse = async (propostaId) => {
    if (!hasPermission('manage_marketplace')) {
      setError('Você não tem permissão para demonstrar interesse.');
      return;
    }

    try {
      setError('');
      
      const demonstrarInteresse = httpsCallable(functions, 'demonstrarInteresse');
      const result = await demonstrarInteresse({
        proposta_id: propostaId
      });
      
      console.log('✅ Interesse demonstrado:', result);
      setSuccess('Interesse demonstrado com sucesso!');
      await loadPropostas();
      
    } catch (error) {
      console.error('❌ Erro ao demonstrar interesse:', error);
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'aberta':
        return Clock;
      case 'em_andamento':
        return MessageCircle;
      case 'finalizada':
        return CheckCircle;
      default:
        return AlertTriangle;
    }
  };

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('pt-BR');
  };

  // Verificar permissão
  if (!hasPermission('view_marketplace') && !hasPermission('manage_marketplace')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Acesso Negado</h3>
          <p className="text-gray-600">Você não tem permissão para visualizar o marketplace.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marketplace</h1>
          <p className="text-gray-600">Encontre e publique oportunidades comerciais</p>
        </div>
        {hasPermission('manage_marketplace') && (
          <button
            onClick={handleCreateProposta}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Nova Proposta</span>
          </button>
        )}
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2" />
          {error}
          <button 
            onClick={() => setError('')}
            className="ml-auto text-red-700 hover:text-red-900"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 flex items-center">
          <CheckCircle className="h-5 w-5 mr-2" />
          {success}
          <button 
            onClick={() => setSuccess('')}
            className="ml-auto text-green-700 hover:text-green-900"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Package className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Abertas</p>
              <p className="text-2xl font-bold text-gray-900">{stats.abertas}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <MessageCircle className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Em Andamento</p>
              <p className="text-2xl font-bold text-gray-900">{stats.em_andamento}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Finalizadas</p>
              <p className="text-2xl font-bold text-gray-900">{stats.finalizadas}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <DollarSign className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Valor Total</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(stats.valor_total)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Buscar propostas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todos os Status</option>
            <option value="aberta">Aberta</option>
            <option value="em_andamento">Em Andamento</option>
            <option value="finalizada">Finalizada</option>
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todas as Categorias</option>
            <option value="alimentos">Alimentos</option>
            <option value="bebidas">Bebidas</option>
            <option value="limpeza">Limpeza</option>
            <option value="equipamentos">Equipamentos</option>
            <option value="servicos">Serviços</option>
            <option value="outros">Outros</option>
          </select>

          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('');
              setCategoryFilter('');
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
          >
            <Filter className="h-4 w-4" />
            <span>Limpar Filtros</span>
          </button>
        </div>
      </div>

      {/* Propostas Grid */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : propostas.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma proposta encontrada</h3>
              <p className="text-gray-600">Não há propostas cadastradas ou que correspondam aos filtros aplicados.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
              {propostas.map((proposta) => {
                const StatusIcon = getStatusIcon(proposta.status);
                
                return (
                  <div key={proposta.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {proposta.titulo}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">
                          {proposta.descricao?.substring(0, 100)}...
                        </p>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        proposta.status === 'aberta' ? 'bg-yellow-100 text-yellow-800' :
                        proposta.status === 'em_andamento' ? 'bg-blue-100 text-blue-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {proposta.status}
                      </span>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <Building className="h-4 w-4 mr-2" />
                        {proposta.empresa_nome || 'Empresa não informada'}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <DollarSign className="h-4 w-4 mr-2" />
                        {formatCurrency(proposta.valor_estimado)}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="h-4 w-4 mr-2" />
                        Prazo: {formatDate(proposta.prazo_entrega)}
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewProposta(proposta)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Visualizar"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {hasPermission('manage_marketplace') && proposta.status === 'aberta' && (
                          <>
                            <button
                              onClick={() => handleDemonstrarInteresse(proposta.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Demonstrar Interesse"
                            >
                              <Heart className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleRespondProposta(proposta)}
                              className="text-green-600 hover:text-green-900"
                              title="Responder"
                            >
                              <MessageCircle className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                      
                      {proposta.interessados && proposta.interessados.length > 0 && (
                        <div className="flex items-center text-sm text-gray-500">
                          <Heart className="h-4 w-4 mr-1" />
                          {proposta.interessados.length} interessado(s)
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                    disabled={pagination.page === pagination.totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Próximo
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando página <span className="font-medium">{pagination.page}</span> de{' '}
                      <span className="font-medium">{pagination.totalPages}</span>
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                        disabled={pagination.page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                        disabled={pagination.page === pagination.totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Próximo
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Proposta Modal */}
      {showModal && (
        <PropostaModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSave={handleSaveProposta}
          proposta={selectedProposta}
          mode={modalMode}
          error={error}
          setError={setError}
        />
      )}
    </div>
  );
};

// Proposta Modal Component
const PropostaModal = ({ isOpen, onClose, onSave, proposta, mode, error, setError }) => {
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    categoria: '',
    valor_estimado: '',
    prazo_entrega: '',
    especificacoes: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (proposta && (mode === 'view' || mode === 'respond')) {
      setFormData({
        titulo: proposta.titulo || '',
        descricao: proposta.descricao || '',
        categoria: proposta.categoria || '',
        valor_estimado: proposta.valor_estimado || '',
        prazo_entrega: proposta.prazo_entrega ? 
          new Date(proposta.prazo_entrega.toDate ? proposta.prazo_entrega.toDate() : proposta.prazo_entrega).toISOString().split('T')[0] : '',
        especificacoes: proposta.especificacoes || ''
      });
    } else {
      setFormData({
        titulo: '',
        descricao: '',
        categoria: '',
        valor_estimado: '',
        prazo_entrega: '',
        especificacoes: ''
      });
    }
  }, [proposta, mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Erro no modal:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">
            {mode === 'create' ? 'Nova Proposta' : 
             mode === 'respond' ? 'Responder Proposta' : 'Visualizar Proposta'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título *
            </label>
            <input
              type="text"
              value={formData.titulo}
              onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={mode === 'view'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Categoria *
            </label>
            <select
              value={formData.categoria}
              onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={mode === 'view'}
            >
              <option value="">Selecione</option>
              <option value="alimentos">Alimentos</option>
              <option value="bebidas">Bebidas</option>
              <option value="limpeza">Limpeza</option>
              <option value="equipamentos">Equipamentos</option>
              <option value="servicos">Serviços</option>
              <option value="outros">Outros</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valor Estimado
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.valor_estimado}
                onChange={(e) => setFormData({ ...formData, valor_estimado: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={mode === 'view'}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prazo de Entrega
              </label>
              <input
                type="date"
                value={formData.prazo_entrega}
                onChange={(e) => setFormData({ ...formData, prazo_entrega: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={mode === 'view'}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição *
            </label>
            <textarea
              value={formData.descricao}
              onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={mode === 'view'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Especificações Técnicas
            </label>
            <textarea
              value={formData.especificacoes}
              onChange={(e) => setFormData({ ...formData, especificacoes: e.target.value })}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={mode === 'view'}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              {mode === 'view' ? 'Fechar' : 'Cancelar'}
            </button>
            {mode !== 'view' && (
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Salvando...' : mode === 'respond' ? 'Enviar Resposta' : 'Salvar'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default MarketplacePage;
